import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { addSection, createPack, updatePack, publishPack } from "../../api/boardbacks.api";
import axios from "axios";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import { useAuth } from "../../useAuth";
import { canEditGovernance, canManageGovernance } from "../../accessControl";

export default function BoardBackEditor() {
  const { user, accessReady = false } = useAuth();
  const canEdit = Boolean(accessReady && canEditGovernance(user));
  const canManage = Boolean(accessReady && canManageGovernance(user));
  const { id } = useParams();
  const [title, setTitle] = useState("");
  const [sectionTitle, setSectionTitle] = useState("");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const createPackMut = useMutation({
    mutationFn: (payload) => createPack(payload),
    onSuccess: (data) => {
      qc.invalidateQueries(["boardbacks"]);
      navigate(`/boardbacks/${data.id}`);
    },
  });

  const updatePackMut = useMutation({
    mutationFn: (payload) => updatePack(id, payload),
    onSuccess: () => {
      qc.invalidateQueries(["boardback", id]);
    },
  });

  const addSectionMut = useMutation({
    mutationFn: (payload) => addSection(id, payload),
    onSuccess: () => qc.invalidateQueries(["boardback", id]),
  });

  const publishMut = useMutation({
    mutationFn: () => publishPack(id),
    onSuccess: () => qc.invalidateQueries(["boardback", id]),
  });

  const uploadFile = async (file) => {
    if (!canEdit || !id || !sectionTitle) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`/boardbacks/sections/${sectionTitle}/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
          }
        },
      });
      qc.invalidateQueries(["boardback", id]);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSave = () => {
    if (!canEdit) return;
    if (id) {
      updatePackMut.mutate({ title });
    } else {
      createPackMut.mutate({ title });
    }
  };

  const handleAddSection = () => {
    if (canEdit && sectionTitle) {
      addSectionMut.mutate({ title: sectionTitle, order: 1 });
      setSectionTitle("");
    }
  };

  return (
    <div>
      <h2>{id ? "Edit Board Pack" : "New Board Pack"}</h2>
      {!canEdit && <p role="status">{accessReady ? "Edit access is required to change this board pack." : "Loading access permissions..."}</p>}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-end" }}>
        <Input
          label="Board pack title"
          value={title}
          onChange={(e) => { if (canEdit) setTitle(e.target.value); }}
          disabled={!canEdit}
          placeholder="Title"
        />
        <Button onClick={handleSave} disabled={!canEdit} variant="primary">
          Save
        </Button>
        {id && (
          <Button onClick={() => { if (canManage) publishMut.mutate(); }} disabled={!canManage} variant="secondary">
            Publish
          </Button>
        )}
      </div>

      {id && (
        <div style={{ marginTop: 16 }}>
          <h3>Sections</h3>
          <div style={{ display: "flex", gap: 6, marginBottom: 8, alignItems: "flex-end" }}>
            <Input
              label="Section title"
              value={sectionTitle}
              onChange={(e) => { if (canEdit) setSectionTitle(e.target.value); }}
              disabled={!canEdit}
              placeholder="Section title"
            />
            <Button onClick={handleAddSection} disabled={!canEdit} variant="secondary">
              Add Section
            </Button>
          </div>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file) uploadFile(file);
            }}
            style={{ border: "1px dashed #aaa", padding: 12, marginTop: 8 }}
          >
            <div>Upload document to current section (enter section id above)</div>
            <input
              type="file"
              disabled={!canEdit}
              accept="application/pdf"
              aria-label="Upload document"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadFile(file);
              }}
            />
            {uploading && <div>Uploading... {uploadProgress}%</div>}
            {error && (
              <div role="alert" style={{ color: "red" }}>
                {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
